import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, Switch, Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox } from "@mui/material";
import { useField } from "formik";

export function FormikTextInput({
    className,
    disabled,
    readOnly,
    isViewDetail,
    fullWidth = true,
    helperText,
    id,
    InputLabelProps,
    InputProps,
    inputProps,
    labelText,
    name,
    placeholder,
    defaultValue,
    type,
    autofocus = false,
    ...props
}) {
    const [field, meta] = useField(name);

    // For date inputs, ensure value is in YYYY-MM-DD format (required by HTML date input)
    const dateField = type === "date" && field.value != null
        ? {
            ...field,
            value:
                field.value instanceof Date
                    ? field.value.toISOString().split("T")[0]
                    : String(field.value).split("T")[0],
        }
        : field;

    return (
        <>
            <TextField
                className={className}
                autoFocus={autofocus}
                disabled={disabled}
                error={meta.touched && meta.error && meta.error}
                fullWidth={fullWidth}
                id={id}
                helperText={
                    meta.error && meta.touched ? meta.error : helperText
                }
                InputLabelProps={{
                    ...InputLabelProps,
                    style: { fontSize: "1.3em" },
                    shrink: true,
                }}
                InputProps={{
                    ...InputProps,
                    readOnly: readOnly || isViewDetail,
                    autoComplete: "false",
                    ...inputProps,
                    tabIndex: isViewDetail ? -1 : 0,
                }}
                sx={disabled ? {
                    '& .MuiInput-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&:before': {
                            borderBottomStyle: 'none',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                    },
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                } : isViewDetail ? {
                    pointerEvents: 'none',
                } : {}}
                label={labelText}
                margin="dense"
                name={name}
                placeholder={placeholder}
                defaultValue={defaultValue}
                type={type}
                required={props.required}
                variant="standard"
                {...dateField}
                {...props}
            />
        </>
    );
}

export function FormikSelectInput({
    children,
    className,
    disabled,
    readOnly,
    isViewDetail,
    fullWidth = true,
    helperText,
    id,
    labelText,
    name,
    defaultValue,
    ...props
}) {
    const [field, meta] = useField(name);
    const { onChange: onChangeProp, ...restProps } = props;

    const handleChange = (event) => {
        if (readOnly) return;
        field.onChange(event);
        onChangeProp?.(event);
    };

    return (
        <>
            <TextField
                className={className}
                defaultValue={defaultValue}
                disabled={disabled}
                error={meta.touched && meta.error && meta.error}
                fullWidth={fullWidth}
                helperText={
                    meta.error && meta.touched ? meta.error : helperText
                }
                id={id}
                InputLabelProps={{
                    style: { fontSize: "1.3rem" },
                    shrink: true,
                }}
                InputProps={{
                    readOnly: readOnly || isViewDetail,
                    tabIndex: isViewDetail ? -1 : 0,
                }}
                sx={disabled ? {
                    '& .MuiInput-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&:before': {
                            borderBottomStyle: 'none',
                        },
                    },
                    '& .Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                    },
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                } : (readOnly || isViewDetail) ? {
                    '& .MuiSelect-select': {
                        cursor: 'default',
                    },
                    pointerEvents: isViewDetail ? 'none' : 'auto',
                } : {}}
                label={labelText}
                margin="dense"
                name={name}
                select
                SelectProps={{
                    native: true,
                    IconComponent: disabled || readOnly || isViewDetail ? () => null : undefined,
                }}
                variant="standard"
                {...field}
                {...restProps}
                onChange={readOnly ? undefined : handleChange}
            >
                {children}
            </TextField>
        </>
    );
}


export const FormikCheckbox = ({ labelText, name, disabled, ...props }) => {
    const [field, meta, helpers] = useField(name);
    const handleChange = (event) => {
        if (disabled) return;
        helpers.setValue(event.target.checked);
    };
    return (
        <FormControlLabel
            control={
                <Checkbox
                    name={name}
                    checked={!!field.value}
                    onChange={handleChange}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    sx={disabled ? { pointerEvents: 'none' } : {}}
                />
            }
            label={labelText}
        />
    );
};

export const FormikYesOrNoInput = ({ labelText, name, onChange, disabled, no = true, yes = true }) => {
    const [field, meta, helpers] = useField(name);

    const handleChange = (event) => {
        if (disabled) return;
        const newValue = event.target.checked;
        if (onChange) {
            onChange(newValue, event);
        }
        helpers.setValue(newValue);
    };

    return (
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} gap={1}>
            {labelText && <FormLabel >{labelText}</FormLabel>}
            <Box display={"flex"} alignItems={"baseline"}>
                {no && <p style={{ fontSize: "14px" }}>No</p>}
                <Switch
                    disabled={disabled}
                    name={name}
                    checked={field.value === true}
                    onChange={handleChange}
                    onBlur={field.onBlur}
                    sx={disabled ? {
                        '& .MuiSwitch-switchBase': {
                            cursor: 'default',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'primary.main',
                            opacity: 0.5,
                        },
                    } : {}}
                    style={disabled ? { pointerEvents: 'none' } : {}}
                />
                {yes && <p style={{ fontSize: "14px" }}>Si</p>}
            </Box>
        </Box>
    );
}
export function FormikRadioGroup({ name, formik, label, info, right, className, disabled, options }) {
    // console.log(formik?.values[name])
    return (
        <>
            <FormControl disabled={disabled}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "max-content" }}>
                    <FormLabel id="demo-controlled-radio-buttons-group">{label}</FormLabel>
                    {/* {info && <div className={`container-info`}> */}
                    {/* <img src={imgInfo} alt="i" style={{ display: "flex" }} /> */}
                    {/* <section className={`data ${right && "right"}`}>{info}</section> */}
                    {/* </div>} */}
                </div>
                <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name={name}
                    value={formik?.values[name]}
                    onChange={e => {
                        formik?.setFieldTouched(name);
                        formik?.handleChange(e);
                    }}
                    onBlur={e => {
                        formik?.setFieldTouched(name);
                        formik?.handleBlur(e);
                    }}
                    style={{ display: "flex", flexWrap: "nowrap", flexDirection: "row" }}
                >
                    {options.length > 0 && options.map((item) => (
                        <FormControlLabel control={<Radio checked={formik?.values[name] === item.value} />} label={item.label} value={item.value} />
                    ))}
                    {/* <FormControlLabel control={<Radio />} label="Si" value={true} />
                    <FormControlLabel control={<Radio />} label="No" value={false} /> */}
                </RadioGroup>
            </FormControl>
            {
                // formik?.touched[name] && formik?.errors[name]  &&  <Typography variant='subtitle1' color={"error"} alignSelf={"start"} fontSize={"12px"} >{formik.errors[name]}</Typography>
            }
        </>
    )
};

export function FormikAutocomplete({ name, labelText, options, optionId, optionLabel, formik, onChange, placeholder }) {
    const [selectedOption, setSelectedOption] = useState(
        options.find((o) => o[optionId] === formik?.values[name]) ?? null
    );

    useEffect(() => {
        const match = options.find((o) => o[optionId] === formik?.values[name]) ?? null;
        setSelectedOption(match);
    }, [formik?.values[name], options]);

    return (
        <Box display={"flex"} flexDirection={"column"} alignItems={"start"} gap={1}>
            {labelText && <FormLabel>{labelText}</FormLabel>}
            <Autocomplete
                style={{ width: "100%" }}
                options={options}
                disableClearable
                getOptionLabel={(option) => option[optionLabel] ?? ""}
                value={selectedOption}
                onChange={(_, selected) => {
                    setSelectedOption(selected);
                    formik.setFieldValue(name, selected?.[optionId] ?? "");
                    onChange?.(selected);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder || "Seleccione uno"}
                        error={formik?.touched[name] && Boolean(formik?.errors[name])}
                        helperText={formik?.touched[name] && formik?.errors[name]}
                        size="small"
                    />
                )}
                isOptionEqualToValue={(option, val) => option[optionId] === val[optionId]}
                noOptionsText="Sin resultados"
            /></Box>
    );
}